<template>
  <ValidationObserver v-slot="{ passes }" ref="validator">
    <b-modal
      id="send-message-modal"
      size="xl"
      footer-class="mt-4"
      @show="handleShowModal"
      @hidden="handleHiddenModal"
    >
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">Send a Message</h3>
            <h6 class="text-muted text-center" v-show="customer.first_name || customer.last_name">
              TO {{ customerFullName | uppercase }}
            </h6>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <b-container class="px-5">
        <h5 class="mb-4">New Message</h5>
        <b-form>
          <section class="channel-section mb-4">
            <b-form-checkbox
              v-model="form.smsEnabled"
              @change="handleSmsToggle"
              class="channel-checkbox"
              :class="{ 'channel-active': form.smsEnabled }"
            >
              SMS
            </b-form-checkbox>
            <div v-if="form.smsEnabled" class="channel-fields mt-3">
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Mobile phone" label-for="mobile">
                    <CustomTelInput name="Mobile" v-model="form.mobile" required />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Message template" label-for="sms-template">
                    <div class="d-flex align-items-center">
                      <b-form-select
                        id="sms-template"
                        :options="smsTemplates"
                        v-model="selectedSmsTemplate"
                        @input="handleSmsTemplateChange"
                        value-field="id"
                        text-field="template_name"
                        class="flex-fill"
                      />
                      <b-spinner v-if="isLoading" small class="ml-2" />
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Message" label-for="sms-message">
                <ValidationProvider
                  name="SMS Message"
                  rules="required|no_mustache"
                  v-slot="validationContext"
                >
                  <b-form-textarea
                    id="sms-message"
                    v-model="form.smsMessage"
                    rows="4"
                    placeholder="Please choose a template or compose a message."
                    spellcheck="true"
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
            </div>
          </section>

          <section class="channel-section mb-3">
            <b-form-checkbox
              v-model="form.emailEnabled"
              @change="handleEmailToggle"
              class="channel-checkbox"
              :class="{ 'channel-active': form.emailEnabled }"
            >
              Email
            </b-form-checkbox>
            <div v-if="form.emailEnabled" class="channel-fields mt-3">
              <b-row>
                <b-col cols="6">
                  <b-form-group label="E-mail address" label-for="email">
                    <ValidationProvider
                      name="E-mail address"
                      rules="required|email"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        id="email"
                        type="email"
                        v-model="form.email"
                        placeholder="Please enter an e-mail address"
                        :state="getValidationState(validationContext)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Message template" label-for="email-template">
                    <div class="d-flex align-items-center">
                      <b-form-select
                        id="email-template"
                        :options="emailTemplates"
                        v-model="selectedEmailTemplate"
                        @input="handleEmailTemplateChange"
                        value-field="id"
                        text-field="template_name"
                        class="flex-fill"
                      />
                      <b-spinner v-if="isLoading" small class="ml-2" />
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Subject" label-for="subject">
                    <ValidationProvider
                      name="Subject"
                      rules="required|no_mustache"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        id="subject"
                        type="text"
                        v-model="form.subject"
                        placeholder="Please enter the subject of e-mail"
                        :state="getValidationState(validationContext)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Message" label-for="email-message">
                <ValidationProvider
                  name="Email Message"
                  rules="required|no_mustache"
                  v-slot="validationContext"
                >
                  <b-form-textarea
                    id="email-message"
                    v-model="form.emailMessage"
                    rows="4"
                    placeholder="Please choose a template or compose a message."
                    spellcheck="true"
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
            </div>
          </section>
        </b-form>
      </b-container>
      <template v-slot:modal-footer="{ cancel }">
        <b-container>
          <div class="d-flex justify-content-end px-4">
            <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
            <b-button
              variant="primary"
              @click="handleSend(passes)"
              class="ml-3"
              :disabled="isLoading || (!form.smsEnabled && !form.emailEnabled)"
            >
              <b-spinner v-if="isLoading" small />
              <span v-else>Send</span>
            </b-button>
          </div>
        </b-container>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import parseMustache from '@/support/parseMustache';
import apiTemplates from '@/api/templates';
import apiOrders from '@/api/orders';
import apiCustomers from '@/api/customers';
import CustomTelInput from '@/components/CustomTelInput.vue';

export default {
  name: 'SendMessageModal',
  components: {
    ValidationProvider,
    ValidationObserver,
    CustomTelInput,
  },
  data() {
    return {
      templates: [],
      selectedSmsTemplate: null,
      selectedEmailTemplate: null,
      form: {
        smsEnabled: false,
        emailEnabled: false,
        email: '',
        mobile: '',
        subject: '',
        smsMessage: '',
        emailMessage: '',
      },
      isLoading: false,
      submitted: false,
    };
  },
  computed: {
    ...mapState({
      selectedCustomer: state => state.customer.selectedCustomer,
      user: state => state.auth.user,
      regions: state => state.regions,
    }),
    ...mapGetters({
      selectedOrder: 'selectedOrder',
      locationSelectorRegions: 'locationSelectorRegions',
      selectedOutlet: 'auth/selectedOutlet',
    }),
    toWhere() {
      return this.selectedCustomer ? 'customer' : 'order';
    },
    customer() {
      if (this.toWhere === 'customer') {
        return this.selectedCustomer;
      }
      return (
        this.selectedOrder?.customer || this.selectedOrder?.order_billing?.billing_address || {}
      );
    },
    outlet() {
      let outlet = {};
      if (this.toWhere === 'customer') {
        outlet = this.selectedOutlet;
      } else {
        outlet = this.selectedOrder?.origin_outlet;
      }

      return { ...outlet, address: this.getAddress(outlet?.address) };
    },
    replyTo() {
      const region = this.regions.find(r => r.id === this.outlet?.region_id);
      if (region?.country_code === 'NZ') return 'hello@dresden.vision';
      return this.outlet?.email;
    },
    regionId() {
      if (this.toWhere === 'customer') {
        return this.selectedCustomer.region.id || this.selectedCustomer.regions?.data[0].id;
      }

      return this.outlet?.region_id;
    },
    customerFullName() {
      return `${this.customer.first_name || ''} ${this.customer.last_name || ''}`;
    },
    smsTemplates() {
      return this.getTemplatesByType('sms');
    },
    emailTemplates() {
      return this.getTemplatesByType('email');
    },
  },
  methods: {
    ...mapMutations({
      updateOrder: 'UPDATE_JOB_IN_JOBS',
      updateCustomer: 'customer/UPDATE_CUSTOMER_IN_CUSTOMERS',
      setSelectedCustomer: 'customer/SET_SELECTED_CUSTOMER',
    }),
    getValidationState({ dirty, validated, valid = null }) {
      if (!this.submitted && !dirty) return null;
      return validated ? valid : null;
    },
    handleSend(passes) {
      this.submitted = true;
      this.$nextTick(() => passes(this.submit));
    },
    getTemplatesByType(type) {
      const defaultTemplate = { id: null, template_name: 'Please select', content_template: '' };
      const typeTemplates = this.templates.filter(t => t.type.slug === type);

      if (this.isLoading) {
        defaultTemplate.template_name = 'Loading...';
      } else if (typeTemplates.length === 0) {
        defaultTemplate.template_name = 'No template available';
      }

      return [defaultTemplate, ...typeTemplates];
    },
    handleShowModal() {
      this.form.email = this.customer.email;
      this.form.mobile = this.customer.mobile;
      this.getTemplates();
    },
    handleHiddenModal() {
      this.templates = [];
      this.selectedSmsTemplate = null;
      this.selectedEmailTemplate = null;
      this.isLoading = false;
      this.submitted = false;
      this.setSelectedCustomer(null);
      this.form = this.$options.data.apply(this).form;
    },
    async getTemplates() {
      this.isLoading = true;
      try {
        const response = await apiTemplates.getMessageTemplates(this.regionId);
        this.templates = response.data?.data;
      } catch (error) {
        this.$store.dispatch('showErrorAlert', error.response.data.error);
      }
      this.isLoading = false;
    },
    handleSmsToggle(enabled) {
      this.selectedSmsTemplate = null;
      if (enabled) this.applyDefaultTemplate('sms');
    },
    handleEmailToggle(enabled) {
      this.selectedEmailTemplate = null;
      if (enabled) this.applyDefaultTemplate('email');
    },
    applyDefaultTemplate(type) {
      const abbreviation = this.selectedOutlet?.abbreviation;
      if (!abbreviation) return;

      const templates = type === 'sms' ? this.smsTemplates : this.emailTemplates;
      const matchName = `order ready (${abbreviation})`.toLowerCase();
      const template = templates.find(t => t.template_name?.toLowerCase() === matchName);
      if (!template) return;

      if (type === 'sms') {
        this.selectedSmsTemplate = template.id;
        this.handleSmsTemplateChange(template.id);
      } else {
        this.selectedEmailTemplate = template.id;
        this.handleEmailTemplateChange(template.id);
      }
    },
    handleSmsTemplateChange(id) {
      const template = this.smsTemplates.find(t => t.id === id);
      if (!template) return;

      const mustacheData = {
        customer: this.customer,
        outlet: this.outlet,
        staff: this.user,
      };

      this.form.smsMessage = parseMustache(template.content_template, mustacheData);
      this.validateFields();
    },
    handleEmailTemplateChange(id) {
      const template = this.emailTemplates.find(t => t.id === id);
      if (!template) return;

      const mustacheData = {
        customer: this.customer,
        outlet: this.outlet,
        staff: this.user,
      };

      this.form.emailMessage = parseMustache(template.content_template, mustacheData);
      this.form.subject = parseMustache(template.subject, mustacheData);
      this.validateFields();
    },
    validateFields() {
      this.$nextTick(() => {
        this.$refs.validator.validate();
      });
    },
    getAddress(address) {
      if (!address) return null;
      return [
        address.address1,
        address.address2,
        address.suburb,
        address.city,
        address.state,
        address.country,
      ]
        .filter(segment => segment)
        .join(', ');
    },
    async submit() {
      this.isLoading = true;

      const promises = [];

      if (this.form.smsEnabled) {
        promises.push({
          type: 'sms',
          message: this.form.smsMessage,
          phone: this.form.mobile,
        });
      }

      if (this.form.emailEnabled) {
        promises.push({
          type: 'email',
          message: this.form.emailMessage,
          email: this.form.email,
          subject: this.form.subject,
          reply_to: this.replyTo,
        });
      }

      try {
        if (this.toWhere === 'customer') {
          await Promise.all(
            promises.map(data => this.sendMessageCustomer(this.selectedCustomer.id, data))
          );
        } else {
          await Promise.all(
            promises.map(data => this.sendMessageOrder(this.selectedOrder.id, data))
          );
        }
        this.$store.dispatch('showSuccessAlert', 'Message sent successfully');
        this.$bvModal.hide('send-message-modal');
      } catch (error) {
        this.isLoading = false;
        this.$store.dispatch('showErrorAlert', error.response.data.message);
      }
    },
    async sendMessageCustomer(id, data) {
      await apiCustomers.sendMessage(id, data);
      this.updateCustomer({
        ...this.selectedCustomer,
        notes: {
          data: [...this.selectedCustomer.notes.data, {}],
        },
      });
    },
    async sendMessageOrder(id, data) {
      await apiOrders.sendMessage(id, data);
      this.updateOrder({
        ...this.selectedOrder,
        notes_count: this.selectedOrder.notes_count + 1,
      });
    },
  },
};
</script>

<style scoped>
.form-control:disabled {
  opacity: 0.7;
}
.channel-checkbox >>> .custom-control-label::before,
.channel-checkbox >>> .custom-control-label::after {
  top: 0;
}
.channel-checkbox >>> .custom-control-label {
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
}
.channel-active >>> .custom-control-label {
  font-weight: bold;
  color: #007bff;
}
.channel-fields {
  padding-left: 1.5rem;
}
</style>
